import { Row } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { TclaimsApproved } from "@/interfaces/claims.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import TaskBreakdownSheet from "@/pages/task-and-claims/components/Approved/TaskBreakDown/ TaskBreakdownSheet";
import { TDailyReport } from "@/schemas/daily-report.schema";
import {
  IClaimBreakDownData,
  ITaskBreakDownData,
} from "@/schemas/task-and-claims";

interface DailyReportClaimsDetailsProps {
  isLoading: boolean;
  data: TclaimsApproved[];
}
const DailyReportClaimsDetails = ({
  isLoading,
  data,
}: DailyReportClaimsDetailsProps): JSX.Element => {
  const { watch, setValue } = useFormContext<TDailyReport>();
  const [breakdownSheet, setBreakdownSheet] = useState({
    isOpen: false,
    itemId: "",
  });
  const [selectedTaskBreakDown, setSelectedTaskBreakDown] = useState<{
    value: ITaskBreakDownData[] | null;
    claimBreakDown: IClaimBreakDownData[] | null;
    selectedIndex: number;
  }>({
    selectedIndex: 0,
    claimBreakDown: null,
    value: null,
  });

  const handleClickTaskBd = ({ row }: { row: Row<TclaimsApproved> }): void => {
    setBreakdownSheet({ isOpen: true, itemId: "" });
    //Watch if the task breakdown is already set
    const taskBreakDown = watch(`taskBreakdowns.${row.index}.taskBreakdown`);

    setSelectedTaskBreakDown({
      value:
        taskBreakDown?.length > 0 ? taskBreakDown : row.original.taskBreakDown,
      selectedIndex: row.index,
      claimBreakDown: row.original.claimBreakDown,
    });
  };

  if (isLoading) {
    return (
      <div>
        <Skeleton className='w-full h-36' />
      </div>
    );
  }

  const columns: ExtendedColumnDef<TclaimsApproved>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),

      enableSorting: false,
      enableHiding: false,
    },
    {
      meta: {
        showRightBorder: true,
        classNameRow: "max-w-[150px]",
      },
      accessorKey: "ItemName",
      header: "Item Name",
      cell: ({ row }) => (
        <div className='text-gray-700 font-semibold text-[16px]'>
          {row?.original?.estimate?.quoteVersion}
        </div>
      ),
    },
    {
      meta: {
        showRightBorder: true,
        className: "max-w-[280px]",
      },
      accessorKey: "Project progress",
      header: "Item Progress",
      cell: ({ row }): React.JSX.Element => (
        <div className='flex items-center gap-2 '>
          <Progress
            segments={[
              {
                value: Number(row.original.projectProgress) || 0,
                color: "#2744bd",
              },
            ]}
          />
          {row?.original?.projectProgress.toFixed(2)}%
        </div>
      ),
      enableSorting: false,
    },
    {
      meta: {
        showRightBorder: true,
        className: "max-w-[80px]",
      },
      accessorKey: "Today's progress",
      header: "Today's progress",
      cell: ({ row }): React.JSX.Element => (
        <p className='text-center '>
          {`${watch(`taskBreakdowns.${row.index}.todayProgress`) ?? 0}%`}
        </p>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "action",
      header: "",
      cell: ({ row }): React.JSX.Element => (
        <div className=''>
          <Button variant='ghost' onClick={() => handleClickTaskBd({ row })}>
            <FileText size={20} className='mr-2' />
            Task Breakdown
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ];

  return (
    <div className='my-8'>
      <h2 className='mb-4 text-lg font-medium'>Task Details</h2>
      <CustomTable
        columns={columns}
        data={data}
        title='task details'
        hide={{
          filters: true,
          functions: true,
        }}
        empty={{}}
      />
      <TaskBreakdownSheet
        sheet={breakdownSheet}
        setSheet={setBreakdownSheet}
        customAction={{
          onSave: (value) => {
            const selectedEstimation =
              data[selectedTaskBreakDown.selectedIndex];
            if (selectedEstimation) {
              setValue(
                `taskBreakdowns.${selectedTaskBreakDown.selectedIndex}.claimsId`,
                selectedEstimation?.claimsId,
              );
              setValue(
                `taskBreakdowns.${selectedTaskBreakDown.selectedIndex}.approvedEstimateId`,
                selectedEstimation?.id,
              );
            }
            //Set Breakdown to form
            setValue(
              `taskBreakdowns.${selectedTaskBreakDown.selectedIndex}.taskBreakdown`,
              value,
            );
            // Extract total progress from breakdown
            const totalProgress = value
              ?.map((val) => val.total)
              .reduce((sum, progress) => sum + progress, 0);
            // Calculate the average progress
            const todayProgress = totalProgress / value.length;
            // Set the today's progress
            setValue(
              `taskBreakdowns.${selectedTaskBreakDown.selectedIndex}.todayProgress`,
              todayProgress,
            );
          }, //Save to hook form
          defaultValues: selectedTaskBreakDown.value || [], //Initial values
          claimBreakDown: selectedTaskBreakDown.claimBreakDown || [], //Claim breakdown
          hideItem: true,
        }}
      />
    </div>
  );
};

export default DailyReportClaimsDetails;
